/**
 * Block editor core component - NOS-25.
 *
 * Renders a list of blocks with drag-and-drop reordering and delegates
 * to block-specific editors. Integrates with BlockPicker for adding blocks.
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { BlockType } from "./blocks/types"
import type { PageSection } from "@/templates/types"
import { getBlockConfig } from "./blocks/registry"
import { BlockEditorSlot } from "./blocks/editors/BlockEditorSlot"
import { BlockPicker } from "./BlockPicker"
import { useState } from "react"

export interface BlockEditorProps {
  blocks: PageSection[]
  onUpdateBlock: (sectionId: string, content: Record<string, unknown>) => void
  onReorderBlocks: (newOrder: PageSection[]) => void
  onAddBlock: (type: BlockType, afterIndex: number) => void
  onRemoveBlock?: (sectionId: string) => void
  /** Block types that cannot be reordered (e.g. hero first, footer last) */
  lockedTypes?: BlockType[]
  /** Whether to show add-block button and block picker */
  canAddBlocks?: boolean
}

function SortableBlockRow({
  block,
  isLocked,
  onRemove,
  children,
}: {
  block: PageSection
  isLocked: boolean
  onRemove?: (id: string) => void
  children: React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id, disabled: isLocked })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const config = getBlockConfig(block.section_type as BlockType)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex gap-2 rounded-lg border bg-background ${
        isDragging ? "opacity-50 shadow-lg" : ""
      }`}
    >
      <div className="flex flex-col items-center border-r bg-muted/30 py-2">
        {!isLocked && (
          <button
            type="button"
            className="touch-none cursor-grab active:cursor-grabbing p-1"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => onRemove(block.id)}
            aria-label="Remove block"
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>
      <div className="flex-1 min-w-0 py-2 pr-2">
        <div className="mb-1 text-xs font-medium text-muted-foreground">
          {config.label}
        </div>
        {children}
      </div>
    </div>
  )
}

export function BlockEditor({
  blocks,
  onUpdateBlock,
  onReorderBlocks,
  onAddBlock,
  onRemoveBlock,
  lockedTypes = ["hero", "footer"],
  canAddBlocks = true,
}: BlockEditorProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [insertAfterIndex, setInsertAfterIndex] = useState(0)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const sortedBlocks = [...blocks].sort((a, b) => a.order_index - b.order_index)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sortedBlocks.findIndex((b) => b.id === active.id)
    const newIndex = sortedBlocks.findIndex((b) => b.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(sortedBlocks, oldIndex, newIndex)
    onReorderBlocks(reordered)
  }

  const handleAddClick = (afterIndex: number) => {
    setInsertAfterIndex(afterIndex)
    setPickerOpen(true)
  }

  const handlePickBlock = (type: BlockType) => {
    onAddBlock(type, insertAfterIndex)
    setPickerOpen(false)
  }

  return (
    <div className="space-y-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedBlocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {sortedBlocks.map((block) => {
              const isLocked = lockedTypes.includes(
                block.section_type as BlockType
              )
              return (
                <SortableBlockRow
                  key={block.id}
                  block={block}
                  isLocked={isLocked}
                  onRemove={onRemoveBlock}
                >
                  <BlockEditorSlot
                    block={block}
                    onChange={(content) =>
                      onUpdateBlock(block.id, content)
                    }
                  />
                </SortableBlockRow>
              )
            })}
          </div>
        </SortableContext>
      </DndContext>

      {canAddBlocks && (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddClick(sortedBlocks.length - 1)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add block
          </Button>
        </div>
      )}

      <BlockPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickBlock}
      />
    </div>
  )
}
